import React, { useState, useCallback } from "react";
import { StyleSheet, ScrollView, TouchableOpacity } from "react-native";
import { object, string } from "@lib/util/yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { View, Wrapper, Card, Text } from "@components/atoms/Themed";
import ErrorDialog from "@components/molecules/dialog/ErrorDialog";
import Toast from "@components/atoms/Toast";
import InputAccessoryView from "@components/atoms/InputAccessoryView";
import KeyboardAvoidingView from "@components/molecules/KeyboardAvoidingView";
import { TextField, ErrorText } from "@components/molecules/TextInput";
import { graphql, useFragment, useMutation } from "react-relay/hooks";
import { SnsAssociationYoutube$key } from "@generated/SnsAssociationYoutube.graphql";
import { SnsAssociationYoutubeMutation } from "@generated/SnsAssociationYoutubeMutation.graphql";
import Spacer from "@components/atoms/Spacer";
import { KeyboardId, YoutubeUrlPattern } from "@constants/App";
import GradientButton from "@components/atoms/GradientButton";
import Divider from "@components/atoms/Divider";
import openURL from "@lib/util/openUrl";
import Icon from "@components/atoms/Icon";
import Colors from "@constants/Colors";

const tiktokQuery = graphql`
  fragment SnsAssociationYoutube on Influencer {
    profile {
      ytChannel
    }
  }
`;

const updateQuery = graphql`
  mutation SnsAssociationYoutubeMutation(
    $input: UpdateInfluencerInfoMutationInput!
  ) {
    updateInfluencerInfo(input: $input) {
      __typename
      ... on Influencer {
        profile {
          ytChannel
        }
      }
      ... on UpdateInfluencerInfoError {
        message
      }
    }
  }
`;

type InputData = {
  ytChannel: string | null;
};

export default function SnsAssociationYoutube({
  youtubeFragment,
}: {
  youtubeFragment: SnsAssociationYoutube$key;
}) {
  const { profile } = useFragment(tiktokQuery, youtubeFragment);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [commit] = useMutation<SnsAssociationYoutubeMutation>(updateQuery);
  const [changed, setChanged] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<InputData>({
    defaultValues: {
      ytChannel: profile.ytChannel,
    },
    mode: "all",
    resolver: yupResolver(
      object().shape({
        ytChannel: string()
          .default(null)
          .trim()
          .url("存在しないURLです")
          .matches(YoutubeUrlPattern, "チャンネルURLを入力してください")
          .nullable()
          .toNull(),
      })
    ),
  });

  const save = useCallback(async () => {
    await handleSubmit(async ({ ytChannel }: InputData) => {
      setLoading(true);
      await new Promise<void>((resolve) => {
        commit({
          variables: {
            input: { ytChannel },
          },
          onCompleted({ updateInfluencerInfo }) {
            if (
              updateInfluencerInfo.__typename === "UpdateInfluencerInfoError"
            ) {
              setError(updateInfluencerInfo.message);
            } else {
              setMessage("保存しました");
            }
            resolve();
          },
        });
      });
      setLoading(false);
    })();
  }, [commit, handleSubmit]);

  return (
    <View style={styles.container}>
      <Spacer height={8} />
      <KeyboardAvoidingView>
        <ScrollView>
          <Wrapper>
            <Card style={styles.content}>
              <TextField
                control={control}
                inputAccessoryViewID={KeyboardId}
                label="チャンネルURL"
                name="ytChannel"
                onChange={(value: string) => {
                  setChanged(value !== profile.ytChannel);
                }}
                placeholder="https://www.youtube.com/"
                type="url"
              />
              {!isValid && errors.ytChannel !== undefined && (
                <ErrorText error={errors.ytChannel.message} />
              )}
              <Spacer height={16} />
              <TouchableOpacity
                onPress={() => openURL("https://youtube.com/")}
                style={styles.open}
              >
                <Text>YouTubeを開く</Text>
                <Spacer width={4} />
                <Icon color={Colors.gray} name="outer" size={16} />
              </TouchableOpacity>
            </Card>
          </Wrapper>
        </ScrollView>
      </KeyboardAvoidingView>

      <Divider />
      <Card style={styles.footer}>
        <GradientButton
          disabled={!isValid || !changed}
          loading={loading}
          onPress={save}
          title="保存する"
        />
      </Card>

      {message !== null && (
        <Toast message={message} onClose={() => setMessage(null)} />
      )}
      {error !== null && (
        <ErrorDialog message={error} onClose={() => setError(null)} />
      )}
      <InputAccessoryView nativeID={KeyboardId} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  main: {
    flex: 1,
  },
  footer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  open: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },
});
